import {
  DashboardHistoryData,
  VehicleInfo,
  TimeSeriesData,
  HistoryStatistics,
  SpeedDistribution,
  AltitudeProfile,
  ChartConfig,
  ChartDataset,
} from '../models/dashboard-history.model';
import {
  StoreHistory,
  FrameHistoryAdapted,
} from '../../../models/manager-content-vehicle-history-drawer.model';
import { formatDateTrackerDate, formatDateTrackerTime } from './format-dates.helper';
import { convertSecondsToTimeString } from 'src/app/vehicles/components/content-panel-vehicles/helpers/convert-seconds-to-time-string';

/**
 * Procesa los datos del historial para generar información del dashboard
 */
export function processDashboardData(storeHistory: StoreHistory): DashboardHistoryData {
  const vehicleInfo = extractVehicleInfo(storeHistory);
  const timeSeriesData = extractTimeSeriesData(storeHistory.detailsHistory.frames);
  const statistics = calculateStatistics(storeHistory);
  const speedDistribution = calculateSpeedDistribution(timeSeriesData);
  const altitudeProfile = calculateAltitudeProfile(timeSeriesData);

  return {
    vehicleInfo,
    timeSeriesData,
    statistics,
    speedDistribution,
    altitudeProfile,
  };
}

/**
 * Extrae información básica del vehículo
 */
function extractVehicleInfo(storeHistory: StoreHistory): VehicleInfo {
  const details = storeHistory.detailsHistory;
  return {
    imei: storeHistory.unit.GNSSIMEI,
    unitName: storeHistory.unit.unitName,
    startDate: details.start_date,
    endDate: details.end_date,
    totalDuration: convertSecondsToTimeString(
      details.duration_ignition_on_s + details.duration_ignition_off_s,
      { shortFormat: false, showZeros: true }
    ),
  };
}

/**
 * Convierte frames a datos de series temporales
 */
function extractTimeSeriesData(frames: FrameHistoryAdapted[]): TimeSeriesData[] {
  return frames.map((frame) => ({
    timestamp: frame.date,
    speed: frame.speed,
    altitude: frame.altitude,
    latitude: frame.latitude,
    longitude: frame.longitude,
    direction: frame.direction,
  }));
}

/**
 * Calcula estadísticas generales del historial
 */
function calculateStatistics(storeHistory: StoreHistory): HistoryStatistics {
  const details = storeHistory.detailsHistory;
  const frames = details.frames;
  
  const altitudes = frames.map(f => f.altitude).filter(alt => alt > 0);
  const minAltitude = altitudes.length > 0 ? Math.min(...altitudes) : 0;
  const maxAltitude = altitudes.length > 0 ? Math.max(...altitudes) : 0;
  const avgAltitude = altitudes.length > 0 ? altitudes.reduce((a, b) => a + b, 0) / altitudes.length : 0;

  return {
    totalDistance: details.total_distance_km,
    maxSpeed: details.max_speed,
    avgSpeed: details.avg_speed,
    minAltitude: Math.round(minAltitude),
    maxAltitude: Math.round(maxAltitude),
    avgAltitude: Math.round(avgAltitude),
    movementTime: details.movement_time,
    stoppedTime: details.stopped_time,
    idleTime: details.idle_time,
    totalStops: details.valid_stops,
    totalEvents: details.events.length,
  };
}

/**
 * Calcula la distribución de velocidades
 */
function calculateSpeedDistribution(timeSeriesData: TimeSeriesData[]): SpeedDistribution[] {
  const speedRanges = [
    { min: 0, max: 10, label: '0-10 km/h', color: '#4CAF50' },
    { min: 10, max: 30, label: '10-30 km/h', color: '#8BC34A' },
    { min: 30, max: 50, label: '30-50 km/h', color: '#FFEB3B' },
    { min: 50, max: 80, label: '50-80 km/h', color: '#FF9800' },
    { min: 80, max: 120, label: '80-120 km/h', color: '#FF5722' },
    { min: 120, max: Infinity, label: '120+ km/h', color: '#F44336' },
  ];

  const totalPoints = timeSeriesData.length;
  const distribution = speedRanges.map((range) => {
    const count = timeSeriesData.filter(
      (data) => data.speed >= range.min && data.speed < range.max
    ).length;
    
    return {
      speedRange: range.label,
      count,
      percentage: totalPoints > 0 ? Math.round((count / totalPoints) * 100) : 0,
      color: range.color,
    };
  });

  return distribution.filter(d => d.count > 0);
}

/**
 * Calcula el perfil de altitud
 */
function calculateAltitudeProfile(timeSeriesData: TimeSeriesData[]): AltitudeProfile[] {
  let cumulativeDistance = 0;
  
  return timeSeriesData.map((data, index) => {
    if (index > 0) {
      const prevData = timeSeriesData[index - 1];
      const distance = calculateDistance(
        prevData.latitude,
        prevData.longitude,
        data.latitude,
        data.longitude
      );
      cumulativeDistance += distance;
    }
    
    return {
      timestamp: data.timestamp,
      altitude: data.altitude,
      distance: cumulativeDistance,
    };
  });
}

/**
 * Calcula la distancia entre dos puntos usando la fórmula de Haversine
 */
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Radio de la Tierra en km
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Genera configuración para gráfico de velocidad vs tiempo
 */
export function createSpeedChartConfig(timeSeriesData: TimeSeriesData[]): ChartConfig {
  const labels = timeSeriesData.map((data) => {
    const date = new Date(data.timestamp);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  });
  
  const speedData = timeSeriesData.map((data) => data.speed);
  const maxSpeed = Math.max(...speedData);
  const avgSpeed = speedData.reduce((a, b) => a + b, 0) / speedData.length;
  
  const dataset: ChartDataset = {
    label: 'Velocidad (km/h)',
    data: speedData,
    borderColor: '#2196F3',
    backgroundColor: 'rgba(33, 150, 243, 0.2)',
    borderWidth: 3,
    fill: true,
    tension: 0.4,
    pointBackgroundColor: '#2196F3',
    pointBorderColor: '#ffffff',
    pointBorderWidth: 2,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointHoverBackgroundColor: '#1976D2',
    pointHoverBorderColor: '#ffffff',
    pointHoverBorderWidth: 3,
  } as any;

  return {
    type: 'line',
    data: {
      labels,
      datasets: [dataset],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      animation: {
        duration: 2000,
        easing: 'easeInOutQuart',
      },
      plugins: {
        title: {
          display: true,
          text: 'Velocidad vs Tiempo',
          font: {
            size: 16,
            weight: 'bold',
          },
          color: '#333',
          padding: 20,
        },
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
            },
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: '#2196F3',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            title: (context: any) => {
              return `Tiempo: ${context[0].label}`;
            },
            label: (context: any) => {
              const speed = context.parsed.y;
              const percentage = ((speed / maxSpeed) * 100).toFixed(1);
              return [
                `Velocidad: ${speed.toFixed(1)} km/h`,
                `${percentage}% de velocidad máxima`,
                `Promedio: ${avgSpeed.toFixed(1)} km/h`
              ];
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Tiempo',
            font: {
              size: 14,
              weight: 'bold',
            },
            color: '#666',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
            lineWidth: 1,
          },
          ticks: {
            color: '#666',
            font: {
              size: 11,
            },
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Velocidad (km/h)',
            font: {
              size: 14,
              weight: 'bold',
            },
            color: '#666',
          },
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
            lineWidth: 1,
          },
          ticks: {
            color: '#666',
            font: {
              size: 11,
            },
            callback: function(value: any) {
              return value + ' km/h';
            },
          },
        },
      },
    } as any,
  };
}

/**
 * Genera configuración para gráfico de altitud vs tiempo
 */
export function createAltitudeChartConfig(altitudeProfile: AltitudeProfile[]): ChartConfig {
  const labels = altitudeProfile.map((data) => {
    const date = new Date(data.timestamp);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  });
  
  const altitudeData = altitudeProfile.map((data) => data.altitude);
  const maxAltitude = Math.max(...altitudeData);
  const minAltitude = Math.min(...altitudeData);
  const avgAltitude = altitudeData.reduce((a, b) => a + b, 0) / altitudeData.length;
  
  const dataset: ChartDataset = {
    label: 'Altitud (m)',
    data: altitudeData,
    borderColor: '#4CAF50',
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    borderWidth: 3,
    fill: true,
    tension: 0.4,
    pointBackgroundColor: '#4CAF50',
    pointBorderColor: '#ffffff',
    pointBorderWidth: 2,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointHoverBackgroundColor: '#388E3C',
    pointHoverBorderColor: '#ffffff',
    pointHoverBorderWidth: 3,
  } as any;

  return {
    type: 'line',
    data: {
      labels,
      datasets: [dataset],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      animation: {
        duration: 2000,
        easing: 'easeInOutQuart',
      },
      plugins: {
        title: {
          display: true,
          text: 'Altitud vs Tiempo',
          font: {
            size: 16,
            weight: 'bold',
          },
          color: '#333',
          padding: 20,
        },
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
            },
          },
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: '#4CAF50',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            title: (context: any) => {
              return `Tiempo: ${context[0].label}`;
            },
            label: (context: any) => {
              const altitude = context.parsed.y;
              const elevationGain = altitude - minAltitude;
              return [
                `Altitud: ${altitude.toFixed(1)} m`,
                `Ganancia de elevación: ${elevationGain.toFixed(1)} m`,
                `Promedio: ${avgAltitude.toFixed(1)} m`,
                `Rango: ${minAltitude.toFixed(1)} - ${maxAltitude.toFixed(1)} m`
              ];
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Tiempo',
            font: {
              size: 14,
              weight: 'bold',
            },
            color: '#666',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
            lineWidth: 1,
          },
          ticks: {
            color: '#666',
            font: {
              size: 11,
            },
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Altitud (m)',
            font: {
              size: 14,
              weight: 'bold',
            },
            color: '#666',
          },
          beginAtZero: false,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
            lineWidth: 1,
          },
          ticks: {
            color: '#666',
            font: {
              size: 11,
            },
            callback: function(value: any) {
              return value + ' m';
            },
          },
        },
      },
    } as any,
  };
}

/**
 * Genera configuración para gráfico de distribución de velocidades
 */
export function createSpeedDistributionChartConfig(speedDistribution: SpeedDistribution[]): ChartConfig {
  const labels = speedDistribution.map((data) => data.speedRange);
  const counts = speedDistribution.map((data) => data.count);
  const totalCount = counts.reduce((a, b) => a + b, 0);
  const maxCount = Math.max(...counts);
  
  // Colores estandarizados para los rangos de velocidad
  const speedRangeColors: Record<string, string> = {
    '0-10 km/h': '#1b7746', // Verde oscuro
    '10-30 km/h': '#7cb342', // Verde claro
    '30-50 km/h': '#ffca28', // Amarillo
    '50-80 km/h': '#ef6c00', // Naranja
    '80-120 km/h': '#d32f2f', // Rojo
    '120+ km/h': '#b71c1c'   // Rojo más oscuro
  };
  
  // Generar arrays de colores para background y border basados en los labels
  const backgroundColors = labels.map(label => {
    const baseColor = speedRangeColors[label] || '#777777';
    // Convertir a rgba con transparencia para el fondo
    return baseColor + 'CC'; // CC en hex es aproximadamente 0.8 en opacidad
  });
  
  const borderColors = labels.map(label => 
    speedRangeColors[label] || '#777777'
  );
  
  const dataset: ChartDataset = {
    label: 'Frecuencia',
    data: counts,
    backgroundColor: backgroundColors,
    borderColor: borderColors,
    borderWidth: 2,
    borderRadius: 4,
    borderSkipped: false,
  } as any;

  return {
    type: 'bar',
    data: {
      labels,
      datasets: [dataset],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      animation: {
        duration: 1500,
        easing: 'easeInOutQuart',
      },
      plugins: {
        title: {
          display: true,
          text: 'Distribución de Velocidades',
          font: {
            size: 16,
            weight: 'bold',
          },
          color: '#333',
          padding: 20,
        },
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true,
          callbacks: {
            title: (context: any) => {
              return `Rango: ${context[0].label} km/h`;
            },
            label: (context: any) => {
              const count = context.parsed.y;
              const percentage = ((count / totalCount) * 100).toFixed(1);
              const intensity = ((count / maxCount) * 100).toFixed(1);
              return [
                `Frecuencia: ${count} registros`,
                `Porcentaje: ${percentage}% del total`,
                `Intensidad: ${intensity}% del máximo`
              ];
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: 'Rango de Velocidad (km/h)',
            font: {
              size: 14,
              weight: 'bold',
            },
            color: '#666',
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
            lineWidth: 1,
          },
          ticks: {
            color: '#666',
            font: {
              size: 11,
            },
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: 'Frecuencia',
            font: {
              size: 14,
              weight: 'bold',
            },
            color: '#666',
          },
          beginAtZero: true,
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
            lineWidth: 1,
          },
          ticks: {
            color: '#666',
            font: {
              size: 11,
            },
            callback: function(value: any) {
              return value + ' registros';
            },
          },
        },
      },
    } as any,
  };
}